import { useCallback, useEffect, useState } from 'react'
import { Check, ChevronDown, ChevronRight, Copy, Link2 } from 'lucide-react'

import { Button } from 'components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog'
import { Label } from 'components/ui/label'
import { Spinner } from 'components/ui/spinner'
import { toast } from 'components/ui/toast-store'
import { cn } from 'lib/utils'

import * as presentationService from '../services/presentationService'
import type { ShareLink, ShareLinkStatus, SnapshotSummary } from '../types/presentation.types'

/**
 * Share a deck outside the app (MYD-509): a public link to one PUBLISHED
 * version, for lenders, landlords and brokers who have no MyDealTeams login.
 *
 * A link shows a snapshot, never the live deck: the recipient sees what was
 * published, and nothing they are not meant to (the owner was warned about
 * financial figures when publishing). Links expire, can be revoked at once,
 * and record each open, so the owner can see whether the lender looked.
 */
interface ShareDialogProps {
	open: boolean
	presentationId: string
	onClose: () => void
	/** Nothing published yet: hand over to the Publish dialog. */
	onPublish: () => void
}

/** The page to send, absolute: the server answers a path unless it knows its own address. */
export const absoluteShareUrl = (url: string, origin = typeof window === 'undefined' ? '' : window.location.origin) =>
	/^https?:\/\//i.test(url) ? url : `${origin}${url}`

const LIFETIME_LABEL = (days: number) => (days === 1 ? '1 day' : `${days} days`)

const STATUS_STYLE: Record<ShareLinkStatus, string> = {
	active: 'bg-success/10 text-success',
	expired: 'bg-gray-100 text-gray-500',
	revoked: 'bg-destructive/10 text-destructive',
}

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '')
const formatDateTime = (value: string) => new Date(value).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })

/** "Safari on iPad", near enough, from a user agent. */
export const describeBrowser = (userAgent: string | null) => {
	if (!userAgent) return 'Unknown browser'
	const browser = /Edg\//.test(userAgent) ? 'Edge' : /Chrome\//.test(userAgent) ? 'Chrome' : /Firefox\//.test(userAgent) ? 'Firefox' : /Safari\//.test(userAgent) ? 'Safari' : 'A browser'
	const device = /iPad/.test(userAgent) ? 'iPad' : /iPhone/.test(userAgent) ? 'iPhone' : /Android/.test(userAgent) ? 'Android' : /Mac OS X/.test(userAgent) ? 'Mac' : /Windows/.test(userAgent) ? 'Windows' : null
	return device ? `${browser} on ${device}` : browser
}

export function ShareDialog({ open, presentationId, onClose, onPublish }: ShareDialogProps) {
	const [loading, setLoading] = useState(true)
	const [loadError, setLoadError] = useState<string | null>(null)
	const [snapshots, setSnapshots] = useState<SnapshotSummary[]>([])
	const [links, setLinks] = useState<ShareLink[]>([])
	const [ttlChoices, setTtlChoices] = useState<number[]>([1, 7, 30, 90])
	const [version, setVersion] = useState<number | null>(null)
	const [ttlDays, setTtlDays] = useState(7)
	const [creating, setCreating] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [copiedId, setCopiedId] = useState<string | null>(null)
	const [expandedId, setExpandedId] = useState<string | null>(null)

	useEffect(() => {
		if (!open) return
		let cancelled = false
		setLoading(true)
		setLoadError(null)
		setError(null)
		Promise.all([presentationService.listSnapshots(presentationId), presentationService.listShareLinks(presentationId)])
			.then(([published, shared]) => {
				if (cancelled) return
				setSnapshots(published.snapshots)
				setVersion(published.snapshots[0]?.version ?? null)
				setLinks(shared.links)
				setTtlChoices(shared.ttlChoices)
				setTtlDays(shared.defaultTtlDays)
			})
			.catch(() => !cancelled && setLoadError('The share links could not be loaded.'))
			.finally(() => !cancelled && setLoading(false))
		return () => {
			cancelled = true
		}
	}, [open, presentationId])

	const copy = useCallback(async (link: ShareLink) => {
		const url = absoluteShareUrl(link.url)
		try {
			await navigator.clipboard.writeText(url)
			setCopiedId(link.id)
			setTimeout(() => setCopiedId(current => (current === link.id ? null : current)), 2000)
		} catch {
			// No clipboard (an insecure origin, or permission refused): show it to copy by hand.
			toast.info(url)
		}
	}, [])

	const create = async () => {
		if (version === null) return
		setCreating(true)
		setError(null)
		try {
			const link = await presentationService.createShareLink(presentationId, version, ttlDays)
			setLinks(current => [link, ...current])
			await copy(link)
		} catch (cause: any) {
			setError(cause?.response?.data?.message ?? 'The link could not be created.')
		} finally {
			setCreating(false)
		}
	}

	const revoke = async (link: ShareLink) => {
		try {
			const revoked = await presentationService.revokeShareLink(presentationId, link.id)
			setLinks(current => current.map(candidate => (candidate.id === revoked.id ? revoked : candidate)))
		} catch (cause: any) {
			toast.error(cause?.response?.data?.message ?? 'The link could not be turned off.')
		}
	}

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent className="sm:max-w-[560px]">
				<DialogHeader>
					<DialogTitle>Share a link</DialogTitle>
					<DialogDescription>
						Anyone with the link can view a published version of this deck, without signing in. It shows what was published, not the live deck, and stops working
						when it expires or you turn it off.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-5 px-5 py-4">
					{loading ? (
						<div className="flex justify-center py-6">
							<Spinner />
						</div>
					) : loadError ? (
						<p className="text-sm text-destructive">{loadError}</p>
					) : snapshots.length === 0 ? (
						<div className="rounded-md border border-dashed border-gray-300 p-4 text-sm text-gray-700">
							<p className="mb-3">Nothing has been published yet. A link shows a published version, so the person you send it to sees exactly what you approved.</p>
							<Button onClick={onPublish}>Publish a snapshot</Button>
						</div>
					) : (
						<div className="flex flex-wrap items-end gap-3">
							<div className="flex flex-col gap-1.5">
								<Label htmlFor="share-version">Version</Label>
								<select
									id="share-version"
									value={version ?? ''}
									onChange={event => setVersion(Number(event.target.value))}
									className="h-9 rounded-md border border-input bg-background px-2 text-sm"
								>
									{snapshots.map(snapshot => (
										<option key={snapshot.version} value={snapshot.version}>
											Version {snapshot.version}, {formatDate(snapshot.createdAt)}
										</option>
									))}
								</select>
							</div>
							<div className="flex flex-col gap-1.5">
								<Label htmlFor="share-lifetime">Works for</Label>
								<select
									id="share-lifetime"
									value={ttlDays}
									onChange={event => setTtlDays(Number(event.target.value))}
									className="h-9 rounded-md border border-input bg-background px-2 text-sm"
								>
									{ttlChoices.map(days => (
										<option key={days} value={days}>
											{LIFETIME_LABEL(days)}
										</option>
									))}
								</select>
							</div>
							<Button onClick={create} disabled={creating || version === null}>
								<Link2 className="mr-1 h-3.5 w-3.5" />
								{creating ? 'Creating...' : 'Create and copy link'}
							</Button>
						</div>
					)}
					{error && <p className="text-sm text-destructive">{error}</p>}

					{links.length > 0 && (
						<div>
							<h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Links</h3>
							<ul className="max-h-72 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200" aria-label="Share links">
								{links.map(link => {
									const expanded = expandedId === link.id
									return (
										<li key={link.id} className="px-3 py-2 text-sm">
											<div className="flex items-center gap-2">
												<button
													type="button"
													onClick={() => setExpandedId(expanded ? null : link.id)}
													className="flex min-w-0 flex-1 items-center gap-2 text-left"
													aria-expanded={expanded}
													aria-label={`Version ${link.version} link, ${link.status}, opened ${link.accessCount} ${link.accessCount === 1 ? 'time' : 'times'}`}
												>
													{expanded ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-400" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-400" />}
													<span className="font-medium text-gray-900">Version {link.version}</span>
													<span className={cn('rounded-full px-2 py-0.5 text-xs font-medium capitalize', STATUS_STYLE[link.status])}>{link.status}</span>
													<span className="truncate text-xs text-gray-500">
														{link.status === 'revoked'
															? `Turned off ${formatDate(link.revokedAt)}`
															: `${link.status === 'expired' ? 'Expired' : 'Expires'} ${formatDate(link.expiresAt)}`}
														{' · '}
														{link.accessCount === 0 ? 'Not opened yet' : `Opened ${link.accessCount} ${link.accessCount === 1 ? 'time' : 'times'}`}
													</span>
												</button>
												{link.status === 'active' && (
													<>
														<button
															type="button"
															onClick={() => copy(link)}
															className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
															aria-label={`Copy the version ${link.version} link`}
															title="Copy link"
														>
															{copiedId === link.id ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
														</button>
														<button
															type="button"
															onClick={() => revoke(link)}
															className="rounded px-2 py-1 text-xs font-medium text-destructive hover:bg-destructive/10"
														>
															Turn off
														</button>
													</>
												)}
											</div>
											{expanded && (
												<div className="mt-2 pl-6">
													{link.recentAccess.length === 0 ? (
														<p className="text-xs text-gray-500">No one has opened this link yet.</p>
													) : (
														<ol className="space-y-0.5 text-xs text-gray-600" aria-label="Recent opens">
															{link.recentAccess.map((access, index) => (
																<li key={`${access.accessedAt}-${index}`}>
																	{formatDateTime(access.accessedAt)} · {describeBrowser(access.userAgent)}
																</li>
															))}
														</ol>
													)}
												</div>
											)}
										</li>
									)
								})}
							</ul>
						</div>
					)}
				</div>

				<DialogFooter>
					<Button variant="ghost" isOutline onClick={onClose}>
						Done
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default ShareDialog
